import { ElementType, useEffect, useState } from "react"
import styled, { css, keyframes, PolymorphicComponentProps } from "styled-components"
import { CloseStrokeIcon } from "../../icons/CloseStroke"
import { DeprecatedButton, DeprecatedButtonProps } from "../button"

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`

const zoomIn = keyframes`
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`

const zoomOut = keyframes`
  from { transform: scale(1); opacity: 1; }
  to { transform: scale(0.95); opacity: 0; }
`

export const DeprecatedModalOverlay = styled.div<{ zIndex?: number }>`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  background-color: rgba(29, 30, 33, 0.5);
  backdrop-filter: blur(20px);

  z-index: ${(props) => props.zIndex || 100};

  ${() => css`
    animation: ${fadeIn} 0.2s ease-out forwards;
    &[data-state="closed"] {
      animation: ${fadeOut} 0.2s ease-in forwards;
    }
  `}
`

export const DeprecatedModalContainer = styled.section`
  width: 31.5rem;
  max-height: 80%;
  overflow-y: auto;
  position: relative;

  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 1rem;
  border: 1px solid ${({ theme }) => theme.colors.grey.$200};
  padding: 0.875rem;
  pointer-events: auto;

  ${() => css`
    animation: ${zoomIn} 0.2s ease-out forwards;
    &[data-state="closed"] {
      animation: ${zoomOut} 0.2s ease-in forwards;
    }
  `}
`

export const DeprecatedCloseContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`

export type DeprecatedModalProps<C extends ElementType> = PolymorphicComponentProps<
  "web",
  {
    canCloseOutside?: boolean
    isOpen: boolean
    onClose: () => void
    overlayProps?: { as?: React.ElementType } & React.HTMLProps<HTMLDivElement>
    closeButtonProps?: DeprecatedButtonProps<"button">
    zIndex?: number
  },
  C,
  C
>

export const DeprecatedModal = <C extends ElementType>({
  canCloseOutside = true,
  isOpen,
  children,
  onClose,
  overlayProps = {},
  closeButtonProps = {},
  zIndex = 100,
  ...containerProps
}: DeprecatedModalProps<C>) => {
  const [showModal, setShowModal] = useState(isOpen)

  useEffect(() => {
    if (isOpen) {
      setShowModal(true)
    } else {
      const timeout = setTimeout(() => setShowModal(false), 200)
      return () => clearTimeout(timeout)
    }
  }, [isOpen])

  useEffect(() => {
    const keyListener = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        canCloseOutside && onClose()
      }
    }
    document.addEventListener("keydown", keyListener)
    return () => document.removeEventListener("keydown", keyListener)
  }, [canCloseOutside, onClose])

  if (!showModal) {
    return null
  }

  return (
    <DeprecatedModalOverlay
      data-state={isOpen ? "open" : "closed"}
      onClick={canCloseOutside && onClose}
      zIndex={zIndex}
      {...overlayProps}
    >
      <DeprecatedModalContainer
        data-state={isOpen ? "open" : "closed"}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        {...containerProps}
      >
        <DeprecatedCloseContainer>
          <DeprecatedButton
            $variant="round"
            onClick={() => onClose()}
            leftIcon={CloseStrokeIcon}
            {...closeButtonProps}
            isLoading={false}
          />
        </DeprecatedCloseContainer>
        {children}
      </DeprecatedModalContainer>
    </DeprecatedModalOverlay>
  )
}
